import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api-client';
import type { AuditTrailEntry, AuditLogEntry, AuditQueryOptions, PaginatedResponse } from '@/types';

/**
 * Hook to fetch audit trail for a specific record
 */
export function useAuditTrail(tableName: string, recordId: string, limit = 100) {
  return useQuery({
    queryKey: ['auditTrail', tableName, recordId, limit],
    queryFn: () => get<{ success: boolean; data: AuditTrailEntry[] }>(`/audit/trail/${tableName}/${recordId}?limit=${limit}`).then(res => res.data),
    enabled: !!tableName && !!recordId,
  });
}

/**
 * Hook to fetch audit logs with filtering
 */
export function useAuditLogs(options: AuditQueryOptions = {}) {
  const queryParams = new URLSearchParams();
  
  if (options.tableName) queryParams.append('tableName', options.tableName);
  if (options.recordId) queryParams.append('recordId', options.recordId);
  if (options.userId) queryParams.append('userId', options.userId);
  if (options.changeType) queryParams.append('changeType', options.changeType);
  if (options.fieldName) queryParams.append('fieldName', options.fieldName);
  if (options.startDate) queryParams.append('startDate', options.startDate);
  if (options.endDate) queryParams.append('endDate', options.endDate);
  if (options.limit) queryParams.append('limit', options.limit.toString());
  if (options.offset) queryParams.append('offset', options.offset.toString());

  return useQuery({
    queryKey: ['auditLogs', options],
    queryFn: () => get<PaginatedResponse<AuditLogEntry>>(`/audit/logs?${queryParams.toString()}`),
  });
}

/**
 * Hook to fetch critical field changes
 */
export function useCriticalFieldChanges(recordId?: string, startDate?: string, endDate?: string) {
  const queryParams = new URLSearchParams();
  
  if (recordId) queryParams.append('recordId', recordId);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);

  return useQuery({
    queryKey: ['criticalFieldChanges', recordId, startDate, endDate],
    queryFn: () => get<{ success: boolean; data: AuditLogEntry[] }>(`/audit/critical-changes?${queryParams.toString()}`).then(res => res.data),
    enabled: !!recordId || !!startDate || !!endDate,
  });
}

/**
 * Hook to fetch user activity
 */
export function useUserActivity(userId: string, limit = 50) {
  return useQuery({
    queryKey: ['userActivity', userId, limit],
    queryFn: () => get<{ success: boolean; data: AuditLogEntry[] }>(`/audit/user-activity/${userId}?limit=${limit}`).then(res => res.data),
    enabled: !!userId,
  });
}
